/**
 * Classification for refresh-token errors thrown by auth0-spa-js.
 *
 * The decision is keyed off the OAuth error code that auth0-spa-js v2 exposes on
 * `error.error` (e.g. an expired/revoked refresh token throws with
 * `error === 'invalid_grant'`, a MissingRefreshTokenError carries
 * `error === 'missing_refresh_token'`). We deliberately do NOT branch on the
 * error class name / `instanceof`: production bundles minify class names, so
 * those checks are unreliable, whereas the OAuth code string survives bundling.
 *
 * A "definitive" error means the refresh token can never succeed again — the
 * session is genuinely dead and the only correct response is to sign out.
 * Everything else (network failure, timeout, 429/5xx) is "transient" and should
 * be retried before giving up.
 */

export type RefreshErrorKind = 'definitive' | 'transient';

export const DEFINITIVE_AUTH_ERRORS = new Set<string>([
  'invalid_grant',
  'missing_refresh_token',
  'invalid_refresh_token',
  'login_required',
  'consent_required',
]);

/** Reads the OAuth error code auth0-spa-js v2 places on `error.error`, if any. */
export const getAuthErrorCode = (error: unknown): string | undefined => {
  const code = (error as { error?: unknown } | null | undefined)?.error;
  return typeof code === 'string' ? code : undefined;
};

export const classifyRefreshError = (error: unknown): RefreshErrorKind =>
  DEFINITIVE_AUTH_ERRORS.has(getAuthErrorCode(error) ?? '') ? 'definitive' : 'transient';
